import React, { useEffect, useRef, useState } from "react";
import { Box, Input, Stack } from "@mui/joy";
import { isValidHex } from "../../../theme/paletteGenerator";

interface ColorInputProps {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  placeholder?: string;
  size?: "sm" | "md";
}

const ColorInput: React.FC<ColorInputProps> = ({
  value,
  onChange,
  disabled,
  placeholder = "#3b82f6",
  size = "sm",
}) => {
  // Local state tracks the picker's live color while the user is dragging.
  // The parent `value` / `onChange` are only updated on commit (mouse-up).
  const [pickerColor, setPickerColor] = useState(value);
  const pickerRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync local state if the parent value changes externally (e.g. a reset).
  useEffect(() => {
    setPickerColor(value);
  }, [value]);

  // The native DOM `change` event fires only when the picker is committed
  // (mouse released / dialog closed), unlike the `input` event which fires
  // on every drag move. Register once; use a ref to avoid stale closures.
  useEffect(() => {
    const el = pickerRef.current;
    if (!el) return;
    const handleCommit = (e: Event) => {
      onChangeRef.current((e.target as HTMLInputElement).value.toLowerCase());
    };
    el.addEventListener("change", handleCommit);
    return () => el.removeEventListener("change", handleCommit);
  }, []);

  const swatchValid = isValidHex(pickerColor);
  const textValid = isValidHex(value);
  const placeholderValid = !!placeholder && isValidHex(placeholder);
  const swatchColor = swatchValid ? pickerColor : (placeholderValid ? placeholder : null);

  const handleNativePickerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update local state only — gives live swatch feedback without propagating upstream.
    setPickerColor(e.target.value.toLowerCase());
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v && !v.startsWith("#") ? `#${v}` : v);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        component="label"
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: swatchColor ?? "background.level2",
          cursor: disabled ? "not-allowed" : "pointer",
          flexShrink: 0,
          overflow: "hidden",
          position: "relative",
          transition: "transform 0.1s",
          "&:hover": disabled ? {} : { transform: "scale(1.1)" },
        }}
      >
        <input
          ref={pickerRef}
          type="color"
          value={swatchColor ?? "#000000"}
          onChange={handleNativePickerInput}
          disabled={disabled}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        />
      </Box>
      <Input
        size={size}
        value={value}
        onChange={handleTextInput}
        disabled={disabled}
        placeholder={placeholder}
        error={value.length > 1 && !textValid}
        slotProps={{ input: { style: { fontFamily: "monospace" } } }}
        sx={{ flex: 1 }}
      />
    </Stack>
  );
};

export default ColorInput;
